import { useState, useEffect, useCallback, lazy, Suspense, Component, type ReactNode, type ErrorInfo } from 'react'
import { useTranslation } from 'react-i18next'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { FileText, PenTool, Clock, MoreHorizontal } from 'lucide-react'
import { useToast } from './hooks/useToast'
import { ToastContainer } from './components/common/Toast'
import { useOffice } from './hooks/useOffice'
import { useOfficeTheme } from './hooks/useOfficeTheme'
import { useAuth, getAuthToken } from './hooks/useAuth'
import { useDocumentList } from './hooks/useDocuments'
import { useDocumentListSSE } from './hooks/useDocumentListSSE'
import { useActors } from './hooks/useActors'
import { readNegotiationContext, readXmlMetadata, readDocumentBinding, type DocumentBinding } from './hooks/useCustomXmlParts'
import { TaskPaneLayout } from './components/layout/TaskPaneLayout'
import { Header } from './components/layout/Header'
// AccountBar merged into Header — account picker replaces logo
import { LoginPrompt } from './components/auth/LoginPrompt'
import { TemplateList } from './components/templates/TemplateList'
import { SendDocument } from './components/documents/SendDocument'
import { DocumentStatusList } from './components/documents/DocumentStatus'
import { ArchiveDocument } from './components/archive/ArchiveDocument'
import { SendNegotiation } from './components/negotiations/SendNegotiation'
import { DevPanel } from './components/layout/DevPanel'
import { MorePanel } from './components/more/MorePanel'
import { HelpPage } from './components/help/HelpPage'
import { OnboardingSlides, useOnboarding } from './components/onboarding/OnboardingSlides'
import { useOnlineStatus } from './hooks/useOnlineStatus'

// Sprint 4 — Unified template components (lazy-loaded)
const UnifiedTemplatePicker = lazy(() => import('./components/templates/UnifiedTemplatePicker').then(m => ({ default: m.UnifiedTemplatePicker })))
const UnifiedTemplateForm = lazy(() => import('./components/templates/UnifiedTemplateForm').then(m => ({ default: m.UnifiedTemplateForm })))

const ACTIVE_ACTOR_KEY = 'kobsign-word-addin-active-actor'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false, // Task pane doesn't have traditional window focus
    },
  },
})

type TabId = 'templates' | 'sign' | 'status' | 'more'

const allTabs: Array<{ id: TabId; icon: typeof FileText; labelKey: string }> = [
  { id: 'sign', icon: PenTool, labelKey: 'tabs.sign' },
  { id: 'templates', icon: FileText, labelKey: 'tabs.templates' },
  { id: 'status', icon: Clock, labelKey: 'tabs.status' },
  { id: 'more', icon: MoreHorizontal, labelKey: 'tabs.more' },
]

function AppContent() {
  const { t } = useTranslation()
  const { isReady, error: officeError } = useOffice()
  useOfficeTheme()
  const { user, isLoading, isLoggingIn, error: authError, login, logout, isAuthenticated } = useAuth()
  const isOnline = useOnlineStatus()
  const [activeTab, setActiveTab] = useState<TabId>('sign')
  const [signTabMode, setSignTabMode] = useState<'sign' | 'negotiate' | 'archive'>('sign')
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [showHelp, setShowHelp] = useState(false)
  const { toasts, showToast, dismiss: dismissToast } = useToast()
  const { showOnboarding, dismissOnboarding } = useOnboarding()
  const [activeActorId, setActiveActorId] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_ACTOR_KEY),
  )

  const { data: actors } = useActors({ enabled: isAuthenticated })

  // Real-time document list updates via SSE (replaces polling)
  useDocumentListSSE({ token: isAuthenticated ? getAuthToken() : null, enabled: isAuthenticated })

  // Resolve active actor — default to first actor (personal) if none selected
  const resolvedActorId = activeActorId && actors?.some((a) => a.koblink_id === activeActorId)
    ? activeActorId
    : actors?.[0]?.koblink_id ?? undefined

  const handleActorChange = useCallback((actorId: string) => {
    setActiveActorId(actorId)
    localStorage.setItem(ACTIVE_ACTOR_KEY, actorId)
    // Remove all cached data and refetch — ensures no stale data from previous actor
    queryClient.removeQueries()
  }, [])

  // Badge: count pending documents (TanStack Query deduplicates with DocumentStatusList)
  const { data: docData } = useDocumentList({ enabled: isAuthenticated, owner: resolvedActorId })
  const pendingCount = (docData?.results ?? []).filter(
    (doc) => doc.status === 'pending' || doc.status === 'partially_signed',
  ).length

  // Detect active negotiation and document binding on current document
  const [hasActiveNegotiation, setHasActiveNegotiation] = useState(false)
  const [activeBinding, setActiveBinding] = useState<DocumentBinding | null>(null)
  useEffect(() => {
    if (!isReady || !isAuthenticated) return
    let cancelled = false

    // Check DOCX XML for negotiation context (local to this document only).
    // Only show negotiation UI if THIS document has an active negotiation
    // bound to it — don't query backend for unrelated negotiations.
    readNegotiationContext().then((ctx) => {
      if (!cancelled) {
        setHasActiveNegotiation(ctx != null)
      }
    }).catch(() => {
      if (!cancelled) setHasActiveNegotiation(false)
    })

    // Check DOCX binding
    readDocumentBinding().then((binding) => {
      if (!cancelled) {
        const activeStatuses = ['pending', 'partially_signed', 'negotiating']
        setActiveBinding(binding && activeStatuses.includes(binding.status) ? binding : null)
      }
    }).catch(() => {
      if (!cancelled) setActiveBinding(null)
    })
    return () => { cancelled = true }
  }, [isReady, isAuthenticated, resolvedActorId])

  // Derive binding state for UI filtering
  // Also treat hasActiveNegotiation as negotiation binding (covers docs created before binding feature)
  const isNegotiationLocked = activeBinding?.status === 'negotiating' || hasActiveNegotiation
  const isSigningLocked = activeBinding != null && !isNegotiationLocked
  const isDocumentLocked = isNegotiationLocked || isSigningLocked

  // Auto-switch to the active process — locked means no choice
  useEffect(() => {
    if (isNegotiationLocked) {
      setSignTabMode('negotiate')
    } else if (isSigningLocked) {
      setSignTabMode('sign')
    }
  }, [isNegotiationLocked, isSigningLocked])

  // Filter visible tabs — hide Templates when document is locked to active process
  const visibleTabs = isDocumentLocked
    ? allTabs.filter((tab) => tab.id !== 'templates')
    : allTabs

  // Auto-switch actor based on document ownership metadata.
  // If this document was previously sent/archived by a specific actor, switch to match.
  useEffect(() => {
    if (!isReady || !isAuthenticated || !actors?.length) return
    let cancelled = false

    readXmlMetadata().then((meta) => {
      if (cancelled || !meta?.owner_actor_id) return
      // Only switch if the owner actor exists in user's actors and doesn't match current
      if (meta.owner_actor_id !== resolvedActorId && actors.some((a) => a.koblink_id === meta.owner_actor_id)) {
        const ownerActor = actors.find((a) => a.koblink_id === meta.owner_actor_id)
        if (ownerActor) {
          handleActorChange(meta.owner_actor_id)
          showToast('info', t('sign.autoSwitchedActor', { name: ownerActor.display_name }))
        }
      }
    }).catch(() => { /* best-effort */ })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, isAuthenticated, actors])

  // Office not ready yet — show loading but keep DevPanel accessible
  if (!isReady) {
    return (
      <TaskPaneLayout>
        <Header />
        <div className="flex flex-col items-center justify-center flex-1 gap-2 text-[13px] text-neutral-500">
          <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" aria-busy="true" role="status" />
          <span className="sr-only">{t('common.loading')}</span>
          {t('office.officeNotReady')}
        </div>
      </TaskPaneLayout>
    )
  }

  // Office initialization error
  if (officeError) {
    return (
      <TaskPaneLayout>
        <Header />
        <div className="flex items-center justify-center flex-1 px-6 text-center">
          <p className="text-sm text-error-500">{officeError}</p>
        </div>
      </TaskPaneLayout>
    )
  }

  // Auth loading
  if (isLoading) {
    return (
      <TaskPaneLayout>
        <Header />
        <div className="flex items-center justify-center flex-1 text-sm text-neutral-400">
          {t('common.loading')}
        </div>
      </TaskPaneLayout>
    )
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <TaskPaneLayout>
        <Header />
        <LoginPrompt
          onLogin={login}
          isLoggingIn={isLoggingIn}
          error={authError}
        />
      </TaskPaneLayout>
    )
  }

  // Main app — authenticated with tabs
  return (
    <TaskPaneLayout>
      {showOnboarding && <OnboardingSlides onDone={dismissOnboarding} />}
      <Header
        userName={user?.name || user?.email}
        onLogout={logout}
        actors={actors}
        activeActorId={resolvedActorId}
        onActorChange={handleActorChange}
      />

      {/* Offline banner */}
      {!isOnline && (
        <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 flex items-center gap-2" role="alert">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
          <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
            {t('common.offline', 'You are offline. Changes will sync when connection is restored.')}
          </span>
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Help page overlay — shown over all tabs */}
        {showHelp && <HelpPage onClose={() => setShowHelp(false)} />}

        {!showHelp && activeTab === 'templates' && (() => {
          const orgId = actors?.find((a) => a.koblink_id === resolvedActorId)?.actor_type === 'ORGANIZATION'
            ? resolvedActorId
            : undefined

          // Sprint 4: Unified template flow — picker → form
          if (selectedTemplateId) {
            return (
              <Suspense fallback={<div className="flex items-center justify-center flex-1 text-sm text-neutral-400">{t('common.loading')}</div>}>
                <UnifiedTemplateForm
                  templateId={selectedTemplateId}
                  organizationId={orgId}
                  onBack={() => setSelectedTemplateId(null)}
                  onSuccess={() => {
                    setSelectedTemplateId(null)
                    setActiveTab('status')
                  }}
                />
              </Suspense>
            )
          }

          return (
            <Suspense fallback={
              <TemplateList organizationId={orgId} />
            }>
              <UnifiedTemplatePicker
                organizationId={orgId}
                onSelect={(template) => setSelectedTemplateId(template.id)}
              />
            </Suspense>
          )
        })()}
        {!showHelp && activeTab === 'sign' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Pill navigation — only shown when document is NOT locked to a process */}
            {!isDocumentLocked && (
              <div className="px-3 pt-2.5 pb-1">
                <div className="flex items-center bg-neutral-100 dark:bg-neutral-800 rounded-lg p-0.5">
                  {(['sign', 'negotiate', 'archive'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setSignTabMode(mode)}
                      className={`relative flex-1 px-3.5 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                        signTabMode === mode
                          ? 'bg-white dark:bg-neutral-700 text-primary-600 dark:text-primary-400 shadow-sm'
                          : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300'
                      }`}
                    >
                      {t(`tabs.signModes.${mode}`)}
                      {mode === 'negotiate' && hasActiveNegotiation && signTabMode !== 'negotiate' && (
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-white dark:ring-neutral-900" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {signTabMode === 'sign' && (
              <SendDocument actorId={resolvedActorId} actors={actors} onActorChange={handleActorChange} />
            )}
            {signTabMode === 'negotiate' && (
              <SendNegotiation actorId={resolvedActorId} actors={actors} onActorChange={handleActorChange} showToast={showToast} onNegotiationActive={setHasActiveNegotiation} onSwitchToSigning={() => {
                setActiveBinding(null)
                setSignTabMode('sign')
              }} />
            )}
            {signTabMode === 'archive' && (
              <ArchiveDocument actorId={resolvedActorId} actors={actors} onActorChange={handleActorChange} />
            )}
          </div>
        )}
        {!showHelp && activeTab === 'status' && <DocumentStatusList owner={resolvedActorId} actorId={resolvedActorId} actors={actors} />}
        {!showHelp && activeTab === 'more' && <MorePanel actorId={resolvedActorId} onLogout={logout} onOpenHelp={() => setShowHelp(true)} />}
      </div>

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Tab bar */}
      <nav role="tablist" className="relative flex border-t border-neutral-200 dark:border-neutral-700 bg-surface">
        {visibleTabs.map(({ id, icon: Icon, labelKey }) => (
          <button
            key={id}
            role="tab"
            aria-selected={activeTab === id}
            aria-label={t(labelKey)}
            onClick={() => {
              setActiveTab(id)
              // Clear template selection when navigating away from templates tab
              if (id !== 'templates') setSelectedTemplateId(null)
            }}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] active:scale-95 transition-all ${
              activeTab === id
                ? 'text-primary-600 dark:text-primary-400 font-semibold'
                : 'text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300'
            }`}
          >
            <div className="relative">
              <Icon size={16} />
              {id === 'status' && pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-error-500 text-white text-[10px] font-bold px-0.5">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              )}
            </div>
            <span>{t(labelKey)}</span>
          </button>
        ))}
        {/* Sliding indicator */}
        <div
          className="absolute bottom-0 h-0.5 bg-primary-600 dark:bg-primary-400 transition-all duration-200"
          style={{
            width: `${100 / visibleTabs.length}%`,
            left: `${(visibleTabs.findIndex((tab) => tab.id === activeTab) / visibleTabs.length) * 100}%`,
          }}
        />
      </nav>
    </TaskPaneLayout>
  )
}

/** Catch-all error boundary — shows crash info instead of blank screen */
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('KobSign crash:', error, info)
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-4 text-xs font-mono">
          <h1 className="text-red-600 font-bold mb-2">KobSign crashed</h1>
          <p className="text-red-500 break-all">{this.state.error.message}</p>
          <pre className="mt-2 text-neutral-500 text-[9px] break-all whitespace-pre-wrap">
            {this.state.error.stack}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-3 py-1 bg-primary-600 text-white rounded text-xs"
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppContent />
        <DevPanel />
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
